/** The action affordances for one preflight item — shared by the Preflight tab
 *  row, the Items drawer and the wizard's item panel.
 *
 *  It exists because those three surfaces had drifted into four unlabelled,
 *  equal-weight, identically-sized icon buttons in three different icon
 *  vocabularies, with the irreversible one (Mark done) sitting next to the
 *  common one (Run).
 *
 *  The hierarchy here is: **one primary verb per item, chosen by what the item
 *  can actually do.** An item that can be run leads with Run — marking it done
 *  by hand would skip the work *and* the bookkeeping — and keeps Mark done in
 *  the overflow. An item that cannot run leads with Mark done, which is then its
 *  real verb and has no Run beside it to be confused with. Preview sits next to
 *  the primary as the safe rehearsal of it; View log is history, and lives in
 *  the overflow.
 *
 *  "Can actually do" is `item.run_action`, the server's verdict on which
 *  endpoint would accept the click — NOT `can_auto_run`, which only says the
 *  action could be automated in principle. Driven off feasibility, the bar gave
 *  items like "Materialise standard notifications" a Run that answered
 *  `409 Auto-run is not enabled for this action.`; `run_action: null` now means
 *  no run-style button at all, and `run_blocked_reason` says why in its place.
 *
 *  An offboarding item can also be skipped with a reason (the host passes `onSkip`):
 *  Skip… sits in the overflow beside Mark done, offered on open work only. A skipped
 *  or superseded item, like a completed one, has nothing left to mark done.
 */
import CheckIcon from '@mui/icons-material/Check';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NextPlanOutlinedIcon from '@mui/icons-material/NextPlanOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { PreflightItem } from '@/api/preflight';
import { ownsItsExecute } from '@/features/tour-operators/tabs/bespokeExecute';

import { isOpenStatus } from './status';

interface Props {
  item: PreflightItem;
  canManage: boolean;
  /** A mutation is in flight for THIS item (not for any item on the page). */
  busy: boolean;
  onPreview: () => void;
  /** POST run/ — the item's `run_action` is 'run'. */
  onRun: () => void;
  /** POST execute/ — the item's `run_action` is 'execute' (an explicit ops
   *  click, which the autorun kill-switch does not gate). */
  onExecute: () => void;
  onComplete: () => void;
  onViewLog: () => void;
  /** Offboarding items only: open the Skip dialog (a reason is required). */
  onSkip?: () => void;
}

export function PreflightItemActionBar({
  item,
  canManage,
  busy,
  onPreview,
  onRun,
  onExecute,
  onComplete,
  onViewLog,
  onSkip,
}: Props) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(menuAnchor);
  const close = () => setMenuAnchor(null);
  const pick = (fn: () => void) => () => {
    close();
    fn();
  };

  // Open work only: a completed item is done, and an offboarding item that was
  // skipped or superseded has nothing left to mark done either.
  const openWork = isOpenStatus(item.status);
  const canComplete = canManage && openWork;
  // The run-style verb, if the server offers one and no bespoke control in the
  // item's own detail already owns that exact click (see `bespokeExecute`).
  const runVerb =
    !canManage || item.run_action === null
      ? null
      : item.run_action === 'execute' && ownsItsExecute(item.action)
        ? null
        : item.run_action;
  const primary: 'run' | 'execute' | 'complete' | null =
    runVerb ?? (canComplete ? 'complete' : null);
  // Why there is no Run, said where Run used to be — but only while the item
  // still looks like something to act on.
  const blockedReason =
    canManage && item.run_action === null && item.applicable && openWork
      ? item.run_blocked_reason
      : null;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
      {canManage && (
        <Tooltip title="Preview (dry run) — writes nothing">
          <span>
            <IconButton size="small" disabled={busy} onClick={onPreview} aria-label="Preview">
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {blockedReason && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ maxWidth: 220, textAlign: 'right', lineHeight: 1.3 }}
        >
          {blockedReason}
        </Typography>
      )}

      {(primary === 'run' || primary === 'execute') && (
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={busy}
          startIcon={<PlayArrowIcon />}
          onClick={primary === 'run' ? onRun : onExecute}
          sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
        >
          Run
        </Button>
      )}
      {primary === 'complete' && (
        <Button
          size="small"
          variant="contained"
          color="success"
          disableElevation
          disabled={busy}
          startIcon={<CheckIcon />}
          onClick={onComplete}
          sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
        >
          Mark done
        </Button>
      )}

      <IconButton
        size="small"
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        aria-label="More actions"
        aria-haspopup="menu"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={open} onClose={close}>
        {primary !== 'complete' && canComplete && (
          <MenuItem disabled={busy} onClick={pick(onComplete)}>
            <ListItemIcon>
              <CheckIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Mark done"
              secondary="Records completion without running the action"
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        )}
        {onSkip && canManage && openWork && (
          <MenuItem disabled={busy} onClick={pick(onSkip)}>
            <ListItemIcon>
              <NextPlanOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Skip…"
              secondary="Not doing it — say why"
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        )}
        <MenuItem onClick={pick(onViewLog)}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View log" />
        </MenuItem>
      </Menu>
    </Stack>
  );
}
